/** biome-ignore-all lint/complexity/noForEach: more readable  */
import type { UIMessage } from '@ai-sdk/react';
import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { formatDateTZ, isValid, parseISO } from '@cobliteam/utils/date';
import type { ComponentProps } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { type ThreadSelectOption, useAIAssistant } from '../store';

// Helper function to group threads by time period
const groupThreadsByPeriod = (threads: ThreadSelectOption[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  const sevenDaysAgo = new Date(today);
  const thirtyDaysAgo = new Date(today);

  yesterday.setDate(yesterday.getDate() - 1);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const groups: Record<string, ThreadSelectOption[]> = {
    today: [],
    yesterday: [],
    '7days': [],
    '30days': [],
    older: [],
  };

  threads.forEach((thread) => {
    const createdAt = new Date(thread.createdAt);

    if (createdAt >= today) {
      groups.today.push(thread);
    } else if (createdAt >= yesterday) {
      groups.yesterday.push(thread);
    } else if (createdAt >= sevenDaysAgo) {
      groups['7days'].push(thread);
    } else if (createdAt >= thirtyDaysAgo) {
      groups['30days'].push(thread);
    } else {
      groups.older.push(thread);
    }
  });

  return groups;
};

const formatThreadDateTime = (createdAt: string) => {
  const fromIso = parseISO(createdAt);
  const d = isValid(fromIso) ? fromIso : new Date(createdAt);
  if (!isValid(d)) return '';
  return formatDateTZ(d, 'dd/MM/yyyy HH:mm:ss');
};

// Skeleton components
const NewConversationSkeleton = () => (
  <div className='mt-4 flex items-center gap-2'>
    <div className='h-8 w-full animate-pulse rounded-md bg-gray-4' />
  </div>
);

const ThreadsListSkeleton = () => (
  <div className='flex flex-col space-y-2 px-2 py-6'>
    {Array.from({ length: 10 }, (_, i) => (
      <div key={`thread-skeleton-${i}-${Math.random()}`} className='flex items-center justify-center'>
        <div className='h-6 w-full animate-pulse rounded bg-gray-4' />
      </div>
    ))}
  </div>
);

type AIChatSidebarProps = {
  width: number;
  status: string;
  stop: () => void;
  clearError: () => void;
  setMessages: (messages: UIMessage[]) => void;
};

type SidebarNavItemConfig = {
  path: string;
  icon: ComponentProps<typeof Icon>['name'];
  label: string;
  /** Renders loading skeleton instead of link when threads are loading */
  isChatEntry?: boolean;
  buttonClassName: string;
  iconClassName?: string;
  iconSize?: ComponentProps<typeof Icon>['size'];
};

const SIDEBAR_NAV_ORDER = ['chat', 'files'] as const;

const SIDEBAR_NAV_ITEMS: Record<(typeof SIDEBAR_NAV_ORDER)[number], SidebarNavItemConfig> = {
  chat: {
    path: Location.AI,
    icon: 'message-circle-plus',
    label: 'Nova conversa',
    isChatEntry: true,
    buttonClassName: 'justify-start',
  },
  files: {
    path: Location.AI_FILES,
    icon: 'folder',
    label: 'Arquivos',
    buttonClassName: 'justify-start px-3 py-2',
    iconClassName: 'text-gray-11',
    iconSize: 'sm',
  },
  // automation: {
  //   path: Location.AI_AUTOMATION,
  //   icon: 'timer-reset',
  //   label: 'Automações',
  //   buttonClassName: 'justify-start px-3 py-2',
  //   iconClassName: 'text-gray-11',
  //   iconSize: 'sm',
  // },
};

const AIChatSidebar = ({ width = 240, status, stop, clearError, setMessages }: AIChatSidebarProps) => {
  const { threadsOptions, isLoadingThreads, threadId, setThreadId, resetNewThreadId } = useAIAssistant();
  const history = useHistory();
  const hasThreadsOptions = !!threadsOptions?.length;

  const handleCreateThread = () => {
    Analytics.track('AI - Chat - Open New Conversation', { source: 'sidebar' });
    stop();
    clearError();
    setMessages([]);
    setThreadId('');
    resetNewThreadId();
  };

  const handleSelectThread = (selectedThreadId: string) => {
    if (selectedThreadId === threadId) return;
    Analytics.track('AI - Chat - New Conversation Created', { threadId: selectedThreadId, source: 'sidebar' });
    stop();
    clearError();
    setMessages([]);
    setThreadId(selectedThreadId);
    resetNewThreadId();
    history.push({
      pathname: Location.AI,
      search: `?threadId=${encodeURIComponent(selectedThreadId)}`,
    });
  };

  const handleNavigate = () => {
    setThreadId('');
    resetNewThreadId();
  };

  const groupedThreads = hasThreadsOptions ? groupThreadsByPeriod(threadsOptions) : null;

  const periodLabels: Record<string, string> = {
    today: 'Hoje',
    yesterday: 'Ontem',
    '7days': '7 dias',
    '30days': '30 dias',
    older: 'Mais antigos',
  };

  const renderThreadItem = (thread: ThreadSelectOption, index: number) => {
    const isFirst = index === 0 && status === 'streaming';
    const isSelected = threadId === thread.value;
    const dateTimeTooltip = formatThreadDateTime(thread.createdAt);

    return (
      <div
        key={`${thread.value}-${index}`}
        data-testid='ai-sidebar-thread-item'
        className={cn('cursor-pointer rounded-md px-3 py-1.5 text-gray-12 text-xs transition-colors hover:bg-gray-4', isSelected && 'bg-gray-5')}
        onClick={() => handleSelectThread(thread.value)}
      >
        <Tooltip asChild content={dateTimeTooltip} enable={!!dateTimeTooltip}>
          <div className='line-clamp-1 text-ellipsis'>{isFirst ? 'Respondendo...' : thread.label}</div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div
      data-testid='ai-sidebar'
      style={{ width }}
      className='scrollbar-thin scrollbar-thumb-gray-4 scrollbar-track-gray-2 flex h-full min-w-[240px] flex-col overflow-y-auto border-gray-6 border-r bg-gray-2'
    >
      {/* Top Navigation Menu */}
      <div className='flex flex-col gap-1 border-gray-6 border-b p-3'>
        {SIDEBAR_NAV_ORDER.map((key) => {
          const item = SIDEBAR_NAV_ITEMS[key];
          const isActive = history.location.pathname === item.path;

          if (item.isChatEntry && isLoadingThreads) {
            return <NewConversationSkeleton key={key} />;
          }

          return (
            <Link key={key} to={item.path} onClick={handleNavigate}>
              <Button
                color='default'
                className={item.buttonClassName}
                variant={isActive ? 'surface' : 'ghost'}
                full
                size='sm'
                startIcon={<Icon name={item.icon} size={item.iconSize} className={item.iconClassName} />}
                onClick={item.isChatEntry ? handleCreateThread : undefined}
                data-testid={item.isChatEntry ? 'ai-sidebar-new-conversation' : undefined}
              >
                {item.label}
              </Button>
            </Link>
          );
        })}
      </div>

      {/* Conversations List */}
      <div className='flex-1 overflow-y-auto px-2'>
        {isLoadingThreads ? (
          <ThreadsListSkeleton />
        ) : hasThreadsOptions && groupedThreads ? (
          <div className='flex flex-1 flex-col py-4 text-gray-12'>
            {Object.entries(periodLabels).map(([period, label]) => {
              const threads = groupedThreads[period];
              if (!threads || threads.length === 0) return null;

              return (
                <div key={period} className='mb-4'>
                  <div className='mb-2 px-2 text-gray-10 text-xs tracking-wide'>{label}</div>
                  <div className='flex flex-col gap-1'>{threads.map((thread, index) => renderThreadItem(thread, index))}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className='flex flex-1 translate-y-1/2 flex-col items-center justify-center py-8 text-center opacity-70'>
            <div className='mb-2 flex items-center justify-center'>
              <Icon name='file-question' size='md' className='text-gray-8' />
            </div>
            <span className='font-medium text-gray-11 text-sm'>Nenhuma conversa</span>
            <span className='mt-1 text-gray-9 text-xs'>Inicie uma nova conversa</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIChatSidebar;
